import React, { useCallback, useEffect, useState } from "react";
import { EntityListBox, EmptyStateCard, EmptyStateWrapper } from "./styled";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import EntityCard from "../../../../components/EntityCard";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import useBarriersService from "../../../../services/administration/useBarriersService";
import useToast, { ToastType } from "../../../../hooks/useToast";
import { Barrier } from "../../../../types/Barrier";
import SurfaceCard from "../../../../components/Common/SurfaceCard";
import AddIcon from '@mui/icons-material/Add';
import BarrierDialog, { BarrierDialogFields } from "../../../../components/BarrierDialog";
import { eventBus } from "../../../../common/eventBus";
import { DialogMode } from "../../../../types/DialogTypes";

function BarrierControl() {
    const [barriers, setBarriers] = useState<Barrier[]>([]);

    const { t } = useTranslation("common");
    const { showToast } = useToast();
    const { getAllBarriers, deleteBarrier, triggerBarrier } = useBarriersService();

    const [barrierDialogInfo, setBarrierDialogInfo] = useState<{
        isOpen: boolean;
        mode?: DialogMode;
        barrierId?: number;
        initialValues?: BarrierDialogFields;
    }>({
        isOpen: false,
    });

    const loadBarriers = useCallback(() => {
        getAllBarriers().then(resp => {
            setBarriers(resp);
        }).catch(() => {
            showToast({
                message: t("common.unknownError"),
                type: ToastType.ERROR,
            });
        });
    }, [getAllBarriers, showToast, t]);

    // load barriers on mount
    useEffect(() => {
        loadBarriers();
    }, [loadBarriers]);

    // reload barriers on barrier changes
    useEffect(() => {
        eventBus.on('barrierCreated', loadBarriers);
        eventBus.on('barrierDeleted', loadBarriers);
        eventBus.on('barrierEdited', loadBarriers);

        return () => {
            eventBus.off('barrierCreated', loadBarriers);
            eventBus.off('barrierDeleted', loadBarriers);
            eventBus.off('barrierEdited', loadBarriers);
        };
    }, [loadBarriers]);

    const closeDialog = useCallback(() => setBarrierDialogInfo({ isOpen: false }), []);

    const openBarrierDialog = useCallback((barrier: Barrier, mode: DialogMode) => {
        const { id, locationid, ...initialValues } = barrier;
        setBarrierDialogInfo({
            isOpen: true,
            mode,
            barrierId: id,
            initialValues
        });
    }, []);

    const handleTriggerBarrier = useCallback(async (barrier: Barrier) => {
        showToast({
            message: "",
            confirmMessage: t("administration.locationAdministration.confirmTriggerBarrier", { name: barrier.name }),
            type: ToastType.CONFIRM,
            confirmCallback: async () => {
                try {
                    await triggerBarrier(barrier.id);
                    showToast({
                        message: t("administration.locationAdministration.barrierTriggered"),
                        type: ToastType.SUCCESS,
                    });
                } catch (error) {
                    showToast({
                        message: t("common.unknownError"),
                        type: ToastType.ERROR,
                    });
                }
            },
        });
    }, [t, showToast, triggerBarrier]);

    const handleDeleteBarrier = useCallback(async (barrier: Barrier) => {
        showToast({
            message: "",
            confirmMessage: t("administration.locationAdministration.confirmDeleteBarrier", { name: barrier.name }),
            type: ToastType.CONFIRM,
            confirmCallback: async () => {
                try {
                    await deleteBarrier(barrier.id);
                    showToast({
                        message: t("administration.locationAdministration.barrierDeleted", { name: barrier.name }),
                        type: ToastType.SUCCESS,
                    });
                } catch (error) {
                    showToast({
                        message: t("common.unknownError"),
                        type: ToastType.ERROR,
                    });
                }
            },
        });
    }, [t, showToast, deleteBarrier]);

    return (
        <>
            <SurfaceCard
                title={
                    <Typography variant="h4" noWrap>
                        {t("administration.locationAdministration.barriersControl")}
                    </Typography>
                }
                action={
                    <Tooltip title={t("administration.locationAdministration.barrierDialog.addBarrier")}>
                        <IconButton
                            size="small"
                            onClick={() => setBarrierDialogInfo({ isOpen: true, mode: DialogMode.CREATE })}
                        >
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                }
            >
                {barriers.length === 0 ? (
                    <EmptyStateWrapper>
                        <EmptyStateCard>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="h6">
                                    {t("administration.locationAdministration.noBarriersTitle")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("administration.locationAdministration.noBarriersDescription")}
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => setBarrierDialogInfo({ isOpen: true, mode: DialogMode.CREATE })}
                            >
                                {t("administration.locationAdministration.barrierDialog.addBarrier")}
                            </Button>
                        </EmptyStateCard>
                    </EmptyStateWrapper>
                ) : (
                    <EntityListBox>
                        {barriers.map(barrier => (
                            <Box key={barrier.id}>
                                <EntityCard
                                    title={barrier.name}
                                    hoverActions={[
                                        {
                                            icon: <SettingsRemoteIcon />,
                                            label: t("administration.locationAdministration.operateBarrier"),
                                            onClick: () => handleTriggerBarrier(barrier),
                                        },
                                         {
                                            icon: <VisibilityIcon />,
                                            label: t("common.viewDetails"),
                                            onClick: () => openBarrierDialog(barrier, DialogMode.VIEW),
                                        },
                                        {
                                            icon: <EditIcon />,
                                            label: t("common.edit"),
                                            onClick: () => openBarrierDialog(barrier, DialogMode.EDIT),
                                        },
                                        {
                                            icon: <DeleteIcon />,
                                            label: t("common.delete"),
                                            onClick: () => handleDeleteBarrier(barrier),
                                        },
                                    ]}
                                />
                            </Box>
                        ))}
                    </EntityListBox>
                )}
            </SurfaceCard>
            {
                barrierDialogInfo.isOpen && (
                    <BarrierDialog
                        open={barrierDialogInfo.isOpen}
                        mode={barrierDialogInfo.mode}
                        barrierId={barrierDialogInfo.barrierId}
                        initialValues={barrierDialogInfo.initialValues}
                        onClose={closeDialog}
                    />
                )
            }
        </>
    );
}

export default BarrierControl;
